import debounce from 'lodash/debounce';
import find from 'lodash/find';
import includes from 'lodash/includes';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import moment from 'moment-timezone';
import Config from 'global/common/Config';
import Loader from 'loader-component';
import EventsAPI from '../../api/events';
import MapContainer from '../map/MapContainer';
import EventItem from './EventItem';
import {setNewData} from '../../common/search-actions';
import {clearSpotsData} from '../../common/spots-actions';

const TABLET_WIDTH_BREAKPOINT = 750;

class EventList extends Component {
    constructor(props) {
        super(props);

        this._IS_WIDGET_IFRAME = (includes(window.location.href, 'spothero') && includes(window.location.href, 'widget'));
        this._DATE_SHORT_FORMAT = 'MM/DD/YYYY';
        this._DATE_LONG_FORMAT = 'ddd, MMM D, YYYY';
        this._loadedEvents = [];
        this._onEventSelected = this._onEventSelected.bind(this);
        this._onEventNotFound = this._onEventNotFound.bind(this);
        this._onWindowResize = this._onWindowResize.bind(this);

        this.state = {
            isLoading: true,
            events: [],
            isLargeViewport: (props.container.offsetWidth >= TABLET_WIDTH_BREAKPOINT)
        };
    }

    componentDidMount() {
        const {
            venueId,
            eventTypes
        } = this.props;

        EventsAPI
            .get({venueId, eventTypes})
            .then(({data}) => {
                this._loadedEvents = data.data.results;

                if (this._loadedEvents.length) {
                    window.currentCityTimezone = this._loadedEvents[0].destination.timezone;
                }

                this.setState({
                    isLoading: false,
                    events: this._loadedEvents
                });
            })
            .catch(resp => {
                this.setState({
                    isLoading: false,
                    events: this._loadedEvents
                });
            });

        window.addEventListener('resize', debounce(this._onWindowResize, 100));

        this._onWindowResize();
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            eventId,
            clearSpots,
            setNewSearchData,
            serverDateTimeFormat
        } = this.props;
        const {
            events
        } = this.state;

        if (eventId) {
            if (prevState.events.length !== events.length) {
                const deepLinkedEvent = find(events, {id: eventId});

                if (deepLinkedEvent) {
                    this._onEventSelected(deepLinkedEvent);
                } else {
                    this._onEventNotFound();
                }
            } else {
                this._onEventNotFound();
            }
        }

        if (!prevState.events.length && events.length) {
            const firstEvent = events[0];

            clearSpots();
            setNewSearchData({
                data: {
                    currentPage: 'events',
                    selectedEvent: firstEvent
                },
                params: {
                    latitude: firstEvent.destination.latitude,
                    longitude: firstEvent.destination.longitude,
                    starts: moment(firstEvent.starts).tz(window.currentCityTimezone).format(serverDateTimeFormat),
                    ends: moment(firstEvent.ends).tz(window.currentCityTimezone).format(serverDateTimeFormat),
                    eid: firstEvent.id
                }
            });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._onWindowResize);
    }

    _onWindowResize() {
        const {
            container
        } = this.props;
        const {
            isLargeViewport
        } = this.state;

        // if map was not rendered because screen width too small, but now it's bigger, render map
        if (!isLargeViewport && container.offsetWidth >= TABLET_WIDTH_BREAKPOINT) {
            this.setState({
                isLargeViewport: true
            });
        }
    }

    _onEventNotFound() {
        const {
            eventFallback,
            setNewSearchData
        } = this.props;

        if (!eventFallback) {
            return;
        }

        setNewSearchData({
            data: {
                currentPage: 'search',
                searchType: 'address',
                searchedDestination: null,
                selectedEvent: null,
                venueId: null,
                eventId: null
            },
            params: {
                eid: null
            }
        });
    }

    _onEventSelected(newSelectedEvent) {
        const {
            setNewSearchData,
            clearSpots,
            serverDateTimeFormat,
            selectedEvent
        } = this.props;

        const newData = {
            data: {
                currentPage: 'events',
                selectedEvent: newSelectedEvent
            },
            params: {
                latitude: newSelectedEvent.destination.latitude,
                longitude: newSelectedEvent.destination.longitude,
                starts: moment(newSelectedEvent.starts).tz(window.currentCityTimezone).format(serverDateTimeFormat),
                ends: moment(newSelectedEvent.ends).tz(window.currentCityTimezone).format(serverDateTimeFormat),
                eid: newSelectedEvent.id
            }
        };

        if (!this.state.isLargeViewport) {
            newData.data.currentPage = 'map';
        }

        if (!selectedEvent || selectedEvent.id !== newSelectedEvent.id) {
            clearSpots();
        }

        setNewSearchData(newData);
    }

    render() {
        const {
            siteUrl,
            venueUrl,
            hourSpan,
            onMultiOrNoResultsFound,
            kmlMapLayerURL,
            selectedEvent,
            affiliate
        } = this.props;
        const {
            isLoading,
            events,
            isLargeViewport
        } = this.state;

        if (isLoading) {
            return <Loader />;
        }

        return (
            <div className="SH-Events">
                <div className="SH-EventList-container">
                    {events.length > 0 &&
                        <div className="SH-EventList">
                            {events.map(event => {
                                return (
                                    <EventItem
                                        siteUrl={siteUrl}
                                        venueUrl={venueUrl}
                                        key={event.id}
                                        event={event}
                                        selected={(selectedEvent && selectedEvent.id === event.id)}
                                        onClick={this._onEventSelected}
                                    />
                                );
                            })}
                        </div>
                    }
                    {events.length === 0 &&
                        <div className="SH-EventList-no-results">
                            <h2 className="heading-md">No results.</h2>
                        </div>
                    }
                </div>
                {isLargeViewport &&
                    <MapContainer
                        siteUrl={siteUrl}
                        isSearchable={false}
                        hourSpan={hourSpan}
                        onMultiOrNoResultsFound={onMultiOrNoResultsFound}
                        kmlMapLayerURL={kmlMapLayerURL}
                        isEventPageMap={isLargeViewport}
                        affiliate={affiliate}
                    />
                }
            </div>
        );
    }
}

EventList.propTypes = {
    siteUrl: PropTypes.string.isRequired,
    venueId: PropTypes.number,
    eventId: PropTypes.number,
    venueUrl: PropTypes.string,
    eventFallback: PropTypes.bool,
    selectedEvent: PropTypes.object,
    eventTypes: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    container: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    hourSpan: PropTypes.number.isRequired,
    kmlMapLayerURL: PropTypes.string,
    onMultiOrNoResultsFound: PropTypes.func.isRequired,
    setNewSearchData: PropTypes.func.isRequired,
    clearSpots: PropTypes.func.isRequired,
    serverDateTimeFormat: PropTypes.string,
    affiliate: PropTypes.string
};

const mapStateToProps = state => {
    const {
        search: {
            data: {
                venueId,
                eventId,
                eventFallback,
                selectedEvent
            }
        }
    } = state;

    return {
        venueId,
        eventId,
        eventFallback,
        selectedEvent
    };
};

const mapDispatchToProps = {
    setNewSearchData: setNewData,
    clearSpots: clearSpotsData
};

const enhance = compose(
    Config,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(EventList);



// WEBPACK FOOTER //
// ./widget/src/js/components/event/EventList.jsx